import { repositoryFactory } from '@data';
import {
  selectNextQuestion,
  selectQuestionFromSection as selectQuestionFromSectionAlgorithm,
} from '@core/algorithms';
import { SECTION } from '@config/constants.js';
import {
  computeRetentionProbability,
  getStabilityForRecallCount,
  isReviewDue,
} from '@core/algorithms/scoreDecay.js';
import { adaptiveSessionStateManager } from './AdaptiveSessionStateManager.js';
import { adaptiveEngineCache } from './AdaptiveEngineCache.js';

/**
 * Adaptive Engine Service
 * Orchestrates adaptive question selection and learning flow
 * 
 * Performance Optimizations:
 * - Caches section scores and question limits (5 min TTL)
 * - Reduces redundant database queries
 * - Scales efficiently for 40+ module courses
 */
export class AdaptiveEngine {
  constructor() {
    this.courseRepository = repositoryFactory.getCourseRepository();
    this.progressRepository = repositoryFactory.getProgressRepository();
    this.sessionRepository = repositoryFactory.getSessionRepository();
  }

  /**
   * Calculate dynamic question group size based on recent performance
   * Base: 5 questions per group
   * Range: 3-20 questions
   *
   * Performance-based sizing:
   * - correctRate >= 0.8: Increase to 7-20 (high performer, extend engagement)
   * - correctRate 0.6-0.8: Maintain 5-7 (steady progress)
   * - correctRate < 0.6: Decrease to 3-5 (struggling, show content sooner)
   *
   * @param {number} correctRate - Correct answers / total answers (0-1)
   * @returns {Object} - { sectionLimit, reinforcementLimit, reason }
   */
  calculateDynamicGroupSize(correctRate = 0.5) {
    let sectionLimit = { min: 3, max: 5 };
    let reinforcementLimit = { min: 1, max: 3 };
    let reason = 'maintenance';

    if (correctRate >= 0.8) {
      // High performer: increase group size to reduce content context-switching
      sectionLimit = { min: 7, max: 15 };
      reinforcementLimit = { min: 2, max: 5 };
      reason = 'high_performance';
    } else if (correctRate >= 0.6) {
      // Steady progress: maintain normal sizing
      sectionLimit = { min: 5, max: 7 };
      reinforcementLimit = { min: 1, max: 3 };
      reason = 'steady_progress';
    } else {
      // Struggling: reduce group size to show content sooner for support
      sectionLimit = { min: 3, max: 4 };
      reinforcementLimit = { min: 1, max: 2 };
      reason = 'struggling';
    }

    console.log('[AdaptiveEngine.calculateDynamicGroupSize]', {
      correctRate: (correctRate * 100).toFixed(1) + '%',
      reason,
      sectionLimit,
      reinforcementLimit,
    });

    return { sectionLimit, reinforcementLimit, reason };
  }

  /**
   * Get dynamic question limits for current session
   * Retrieves previously calculated size or calculates from recent performance
   * Results are cached to avoid redundant calculations
   *
   * @param {string} sessionId - Active session ID
   * @returns {Object} - { sectionLimit, reinforcementLimit, reason, fromCache }
   */
  async getDynamicQuestionLimits(sessionId) {
    // Check cache first
    const cached = adaptiveEngineCache.get('dynamicLimits', sessionId);
    if (cached) {
      return { ...cached, fromCache: true, cacheHit: true };
    }

    try {
      const session = await this.sessionRepository.getSessionById(sessionId);
      if (!session) {
        // No session, use defaults
        return {
          ...this.calculateDynamicGroupSize(0.5),
          fromCache: false,
          cacheHit: false,
        };
      }

      // Check if session has pre-calculated group size
      const cachedSize = session.nextQuestionGroupSize || 5;
      
      // For now, we'll use a proportional sizing approach
      // Current size tells us the upper bound for reinforcement
      const performance = await adaptiveSessionStateManager.calculateRecentPerformance(sessionId);
      const sized = this.calculateDynamicGroupSize(performance.correctRate);

      const result = {
        sectionLimit: { min: Math.max(3, Math.floor(cachedSize * 0.7)), max: cachedSize },
        reinforcementLimit: { min: 1, max: Math.ceil(cachedSize * 0.4) },
        reason: sized.reason,
        fromCache: false,
        cacheHit: false,
      };

      // Cache the result for 5 minutes
      adaptiveEngineCache.set('dynamicLimits', {
        sectionLimit: result.sectionLimit,
        reinforcementLimit: result.reinforcementLimit,
        reason: result.reason,
      }, sessionId);

      return result;
    } catch (err) {
      console.warn('[AdaptiveEngine.getDynamicQuestionLimits] Error:', err);
      return {
        ...this.calculateDynamicGroupSize(0.5),
        fromCache: false,
        cacheHit: false,
      };
    }
  }

  /**
   * Check if a section is being revisited (student has been to this section before)
   * @param {string} sectionId - Section to check
   * @param {Object} progress - User progress object
   * @returns {boolean} - True if section has been visited before
   */
  async isSectionRevisit(sectionId, progress) {
    if (!progress) return false;
    const sectionProgress = progress.getSectionProgress?.(sectionId) || progress.sectionProgress?.[sectionId];
    return sectionProgress && (sectionProgress.timesStudied > 0 || sectionProgress.lastStudied);
  }

  /**
   * Build a queued set of questions for the current round.
   * - Dynamically sized questions from the current section (3-15+ based on performance)
   * - Dynamically sized reinforcement questions from lowest-scoring prior section
   * 
   * Sizes adjust based on recent performance:
   * - High performer (80%+): 7-15 from current, 2-5 reinforcement
   * - Steady (60-80%): 5-7 from current, 1-3 reinforcement
   * - Struggling (<60%): 3-4 from current, 1-2 reinforcement (show content sooner)
   * 
   * PERFORMANCE: Results are cached for 10 minutes to avoid redundant queries
   */
  async buildQuestionQueue(userId, courseId, currentSectionId, options = {}) {
    const {
      excludeQuestionIds = [],
      sessionId = null,
      customSectionLimit = null,
      customReinforcementLimit = null,
    } = options;

    // Try cache first (10 min TTL)
    const cacheKey = `${userId}:${courseId}:${currentSectionId}:${excludeQuestionIds.join(',')}`;
    const cached = adaptiveEngineCache.get('questionQueue', cacheKey);
    if (cached) {
      console.log('[buildQuestionQueue] Using cached question queue:', {
        sessionId,
        cacheKey: cacheKey.substring(0, 50) + '...',
        queueSize: cached.length,
      });
      return cached;
    }

    // Get dynamic limits based on recent performance (or use custom overrides)
    let sectionLimit = customSectionLimit;
    let reinforcementLimit = customReinforcementLimit;

    if (!sectionLimit || !reinforcementLimit) {
      try {
        const dynamicLimits = await this.getDynamicQuestionLimits(sessionId);
        sectionLimit = customSectionLimit || dynamicLimits.sectionLimit;
        reinforcementLimit = customReinforcementLimit || dynamicLimits.reinforcementLimit;
        
        console.log('[buildQuestionQueue] Dynamic sizing applied:', {
          sessionId,
          sectionLimit,
          reinforcementLimit,
          reason: dynamicLimits.reason,
        });
      } catch (err) {
        // Fallback to defaults if dynamic calculation fails
        sectionLimit = customSectionLimit || { min: 3, max: 5 };
        reinforcementLimit = customReinforcementLimit || { min: 1, max: 3 };
        console.warn('[buildQuestionQueue] Falling back to default limits:', err);
      }
    }

    const course = await this.courseRepository.getCourseById(courseId);
    if (!course) throw new Error(`Course not found: ${courseId}`);

    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    if (!progress) throw new Error(`Progress not found for user: ${userId}`);

    const currentSection = course.getSectionById(currentSectionId);
    if (!currentSection) throw new Error(`Section not found: ${currentSectionId}`);

    // Helper to shuffle array immutably
    const shuffle = (arr) => {
      const copy = [...arr];
      for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy;
    };

    // 1) Pick dynamically-sized questions from current section
    const currentQuestions = (await this.courseRepository.getQuestionsForSection(courseId, currentSectionId))
      .filter(q => !excludeQuestionIds.includes(q.id));

    console.log('[buildQuestionQueue] Current section questions:', {
      sectionId: currentSectionId,
      total: currentQuestions.length,
      limits: sectionLimit,
    });

    const sectionPickCount = Math.min(
      Math.max(sectionLimit.min, 3),
      Math.max(3, Math.min(sectionLimit.max || 5, currentQuestions.length))
    );

    const selectedCurrentQuestions = shuffle(currentQuestions).slice(0, sectionPickCount);
    
    console.log('[buildQuestionQueue] Selected from current section:', {
      count: selectedCurrentQuestions.length,
      sectionId: currentSectionId,
    });

    // 2) Reinforcement selection: lowest-scoring prior section (if any unlocked)
    const priorSections = course.sections.filter(s => s.index < currentSection.index && progress.isSectionUnlocked(s.id));

    let reinforcementQuestions = [];
    if (priorSections.length > 0) {
      const lowestSection = priorSections.reduce((lowest, section) => {
        const score = progress.getSectionProgress(section.id)?.score || 0;
        const lowestScore = progress.getSectionProgress(lowest.id)?.score || 0;
        return score < lowestScore ? section : lowest;
      }, priorSections[0]);

      const reinforcePool = (await this.courseRepository.getQuestionsForSection(courseId, lowestSection.id))
        .filter(q => !excludeQuestionIds.includes(q.id) && !selectedCurrentQuestions.some(cq => cq.id === q.id));

      const reinforcementCount = Math.min(
        Math.max(reinforcementLimit.min, 1),
        Math.max(1, Math.min(reinforcementLimit.max || 3, reinforcePool.length))
      );

      reinforcementQuestions = shuffle(reinforcePool).slice(0, reinforcementCount);
    }

    // 3) Sort current section questions by difficulty based on section mastery
    // Low mastery (<40%): easier questions first; high mastery (>70%): harder questions first
    const sectionScore = progress.getSectionProgress(currentSectionId)?.score || 0;
    const sortedCurrentQuestions = [...selectedCurrentQuestions].sort((a, b) => {
      const diffA = typeof a.difficulty === 'string'
        ? (a.difficulty === 'hard' ? 1.0 : a.difficulty === 'medium' ? 0.5 : 0.2)
        : (a.difficulty ?? 0.5);
      const diffB = typeof b.difficulty === 'string'
        ? (b.difficulty === 'hard' ? 1.0 : b.difficulty === 'medium' ? 0.5 : 0.2)
        : (b.difficulty ?? 0.5);
      // Below 40% mastery → ascending difficulty (easy first)
      // Above 70% mastery → descending difficulty (hard first)
      // Between 40-70% → neutral ordering (keep shuffled)
      if (sectionScore < 40) return diffA - diffB;
      if (sectionScore > 70) return diffB - diffA;
      return 0;
    });

    // 4) Interleave reinforcement questions randomly throughout (not appended at the end)
    const combined = [...sortedCurrentQuestions];
    reinforcementQuestions.forEach(q => {
      const insertPos = Math.floor(Math.random() * (combined.length + 1));
      combined.splice(insertPos, 0, q);
    });
    const queue = combined;
    
    // Cache the result for 10 minutes (reuse cacheKey from above)
    adaptiveEngineCache.set('questionQueue', queue, cacheKey);
    
    console.log('[buildQuestionQueue] Final queue (cached):', {
      total: queue.length,
      current: sortedCurrentQuestions.length,
      reinforcement: reinforcementQuestions.length,
      sectionScore: `${sectionScore.toFixed(1)}%`,
      sectionLimit,
      reinforcementLimit,
    });
    return queue;
  }

  /**
   * Select next adaptive question for the user
   * Implements the 5-priority selection algorithm
   * PERFORMANCE: Caches course and progress data
   */
  async selectNextAdaptiveQuestion(userId, courseId, currentSectionId, options = {}) {
    const {
      excludeQuestionIds = [],
      warmUpMode = false,
      reinforcementMode = false,
      tagMasteryMap = {}, // HamTestOnline tag mastery for 60/30/10 weighting
    } = options;

    const startTime = performance.now();

    // Get course and progress with caching
    // Try to get cached course data
    let course = adaptiveEngineCache.get('courseData', courseId);
    if (!course) {
      course = await this.courseRepository.getCourseById(courseId);
      if (!course) {
        throw new Error(`Course not found: ${courseId}`);
      }
      adaptiveEngineCache.set('courseData', course, courseId);
    }

    // Try to get cached progress data (shorter TTL since it changes)
    let progress = adaptiveEngineCache.get('progressData', userId, courseId);
    if (!progress) {
      progress = await this.progressRepository.getUserProgress(userId, courseId);
      if (!progress) {
        throw new Error(`Progress not found for user: ${userId}`);
      }
      adaptiveEngineCache.set('progressData', progress, userId, courseId);
    }

    // Check if we're in LINEAR mode (first pass) or ADAPTIVE mode (review)
    const visitedSections = course.sections.filter(s => {
      const sectionProg = progress.getSectionProgress(s.id);
      return sectionProg && sectionProg.questionsAttempted > 0;
    });
    const linearMode = visitedSections.length < course.sections.length;
    
    console.log('[AdaptiveEngine] Learning mode:', {
      mode: linearMode ? 'LINEAR' : 'ADAPTIVE',
      visitedSections: visitedSections.length,
      totalSections: course.sections.length,
    });

    // Get current section
    const currentSection = course.getSectionById(currentSectionId);
    if (!currentSection) {
      throw new Error(`Section not found: ${currentSectionId}`);
    }

    // Get session to check recent questions
    const session = await this.sessionRepository.getActiveSession(userId, courseId);
    const sessionQuestions = Array.isArray(session?.answers)
      ? session.answers.map((answer) => ({
          ...answer,
          correct: answer?.correct ?? answer?.isCorrect ?? false,
        }))
      : [];

    // Prepare sections data for algorithm
    // IMPORTANT: Only include current section and PRIOR unlocked sections
    const currentSectionIndex = course.sections.findIndex(s => s.id === currentSectionId);
    const allSections = course.sections.map((section, index) => {
      const sectionProgress = progress.getSectionProgress(section.id) || { score: 0 };
      const sectionIndex = section.index ?? index;
      return {
        id: section.id,
        index: sectionIndex,
        title: section.title,
        score: sectionProgress.score || 0,
        lastStudied: sectionProgress.lastStudiedAt || sectionProgress.lastStudied || null,
        lastStudiedAt: sectionProgress.lastStudiedAt || sectionProgress.lastStudied || null,
        decayedAmount: sectionProgress.decayedAmount || 0,
      };
    });

    // Prepare questions data for algorithm
    // CRITICAL: Only include questions from current section OR prior unlocked sections
    // NEVER include questions from future sections (sections with index > currentSectionIndex)
    const buildStartTime = performance.now();
    const allQuestions = [];
    let futureSkippedCount = 0;
    let lockedSkippedCount = 0;
    
    for (const section of course.sections) {
      const sectionIndex = course.sections.findIndex(s => s.id === section.id);
      
      // Skip future sections (sections after current section)
      if (sectionIndex > currentSectionIndex) {
        futureSkippedCount++;
        continue;
      }
      
      // Skip locked sections except current
      if (section.id !== currentSectionId && !progress.isSectionUnlocked(section.id)) {
        lockedSkippedCount++;
        continue;
      }

      for (const question of section.questions) {
        // Skip excluded questions
        if (excludeQuestionIds.includes(question.id)) {
          continue;
        }

        const questionProgress = progress.getQuestionProgress(question.id) || {
          score: 0,
          attempts: 0,
          correctAttempts: 0,
          lastAttemptAt: null,
          consecutiveCorrect: 0,
          lastDecayAppliedAt: null,
          timesShown: 0,
          currentScore: 0,
        };

        const timesCorrect = questionProgress.timesCorrect ?? questionProgress.correctAttempts ?? 0;
        const timesIncorrect = questionProgress.timesIncorrect ?? 0;
        const historyLength = Array.isArray(questionProgress.history) ? questionProgress.history.length : 0;
        const normalizedTimesShown = Math.max(
          questionProgress.timesShown ?? 0,
          questionProgress.attempts ?? 0,
          timesCorrect + timesIncorrect,
          historyLength,
        );
        const normalizedScore = questionProgress.currentScore ?? questionProgress.score ?? 0;
        const normalizedLastShown = questionProgress.lastShown ?? questionProgress.lastAttemptAt ?? 0;

        // Get tag mastery for this question's tags
        const questionTags = question.tags || [];
        const tagMasteryValues = questionTags.map(tag => tagMasteryMap[tag]?.finalMastery || 0);

        // Ebbinghaus retention signals (PRD §1.1, §1.4)
        // stabilityS: derived from stored memoryStability when available, otherwise computed
        // from timesCorrect using the default progression table.
        const stabilityS = questionProgress.memoryStability
          ?? getStabilityForRecallCount(timesCorrect);
        // lastSuccessfulRecallMs: prefer the stored field; fall back to lastShown when the
        // question has a positive streak (meaning the last answer was correct).
        const lastSuccessfulRecallMs = questionProgress.lastSuccessfulRecall
          ? new Date(questionProgress.lastSuccessfulRecall).getTime()
          : (timesCorrect > 0 && (questionProgress.streak ?? 0) > 0
              ? (normalizedLastShown ? new Date(normalizedLastShown).getTime() : 0)
              : 0);
        const retentionR = computeRetentionProbability(lastSuccessfulRecallMs, stabilityS);
        // isDue: question is overdue for review per PRD §1.4 priority 1
        const isDue = timesCorrect > 0 && isReviewDue(lastSuccessfulRecallMs, stabilityS);

        // Flatten question + progress for algorithm consumption
        allQuestions.push({
          ...question,
          sectionId: section.id,
          // Flatten progress properties to top level for algorithm
          currentScore: normalizedScore,
          score: normalizedScore,
          timesShown: normalizedTimesShown,
          attempts: normalizedTimesShown,
          lastShown: normalizedLastShown,
          timesCorrect,
          timesIncorrect,
          difficulty: typeof question.difficulty === 'string'
            ? (question.difficulty === 'hard' ? 1.0 : question.difficulty === 'medium' ? 0.5 : 0.2)
            : (question.difficulty || 0.5),
          // Tag mastery for HamTestOnline selection
          tagMasteryValues,
          // Ebbinghaus retention (PRD §1.1)
          stabilityS,
          retentionR,
          isDue,
          // Keep progress for reference
          progress: questionProgress,
        });
      }
    }

    if (allQuestions.length === 0) {
      return null; // No questions available
    }

    const buildDuration = performance.now() - buildStartTime;

    // Log section filtering summary (instead of 33+ individual logs)
    console.log('[AdaptiveEngine] Question pool built:', {
      duration: `${buildDuration.toFixed(0)}ms`,
      currentSection: currentSectionIndex + 1,
      totalSections: course.sections.length,
      futureSkipped: futureSkippedCount,
      lockedSkipped: lockedSkippedCount,
      included: course.sections.length - futureSkippedCount - lockedSkippedCount,
      totalQuestions: allQuestions.length,
    });

    // Find current section data
    const currentSectionData = allSections.find(s => s.id === currentSectionId);
    
    console.log('[AdaptiveEngine.selectNextAdaptiveQuestion] Current section data:', {
      id: currentSectionData?.id,
      index: currentSectionData?.index,
      score: currentSectionData?.score,
    });
    
    // Log available sections and question counts
    const sectionQuestionCounts = {};
    allSections.forEach(section => {
      sectionQuestionCounts[section.id] = allQuestions.filter(q => q.sectionId === section.id).length;
    });
    console.log('[AdaptiveEngine.selectNextAdaptiveQuestion] Available questions by section:', sectionQuestionCounts);
    
    if (!currentSectionData) {
      console.error('[AdaptiveEngine.selectNextAdaptiveQuestion] ERROR: currentSectionData not found!');
      return null;
    }

    // Use the selection algorithm - now returns {question, reason}
    // Pass tagMasteryMap for HamTestOnline 60/30/10 weighting
    // Pass linearMode to adjust question distribution (heavy on current section during first pass)
    const effectiveLinearMode = warmUpMode ? true : reinforcementMode ? false : linearMode;

    const selectedItem = selectNextQuestion(
      currentSectionData,
      allSections,
      allQuestions,
      sessionQuestions,
      { tagMasteryMap, linearMode: effectiveLinearMode, warmUpMode, reinforcementMode }
    );

    console.log('[AdaptiveEngine.selectNextAdaptiveQuestion] Selected item:', {
      questionId: selectedItem?.question?.id,
      reason: selectedItem?.reason,
      sectionId: selectedItem?.sectionId,
    });

    // Final validation: Ensure selected question is not from a future section
    if (selectedItem?.question) {
      const questionSectionIndex = course.sections.findIndex(s => s.id === selectedItem.sectionId);
      if (questionSectionIndex > currentSectionIndex) {
        console.error('[AdaptiveEngine] ERROR: Selected question from future section!', {
          questionId: selectedItem.question.id,
          questionSectionId: selectedItem.sectionId,
          questionSectionIndex,
          currentSectionId,
          currentSectionIndex,
        });
        // Fall back to current section question
        const fallbackQuestion = allQuestions.find(q => q.sectionId === currentSectionId);
        if (fallbackQuestion) {
          return {
            question: fallbackQuestion,
            reason: 'current',
            sectionId: currentSectionId,
          };
        }
        return null;
      }
    }

    // Return question WITH reason for adaptive UI feedback
    if (selectedItem) {
      const duration = performance.now() - startTime;
      console.log(`[AdaptiveEngine] Question selected in ${duration.toFixed(0)}ms`, {
        reason: selectedItem.reason,
        questionId: selectedItem.question?.id?.substring(0, 20),
      });
      
      return {
        question: selectedItem.question,
        reason: selectedItem.reason || 'current',  // Default to 'current' if not specified
        sectionId: selectedItem.sectionId,
      };
    }

    return null;
  }

  /**
   * Select question from specific section
   * Used during initial section learning
   */
  async selectQuestionFromSection(userId, courseId, sectionId, excludeQuestionIds = []) {
    const questions = await this.courseRepository.getQuestionsForSection(courseId, sectionId);
    const progress = await this.progressRepository.getUserProgress(userId, courseId);

    if (!progress) {
      throw new Error(`Progress not found for user: ${userId}`);
    }

    // Build question data
    const questionData = questions
      .filter(q => !excludeQuestionIds.includes(q.id))
      .map(question => {
        const rawQuestionProgress = progress.getQuestionProgress(question.id) || {
          score: 0,
          attempts: 0,
          correctAttempts: 0,
          lastAttemptAt: null,
          consecutiveCorrect: 0,
          lastDecayAppliedAt: null,
        };

        const timesCorrect = rawQuestionProgress.timesCorrect ?? rawQuestionProgress.correctAttempts ?? 0;
        const timesIncorrect = rawQuestionProgress.timesIncorrect ?? 0;
        const historyLength = Array.isArray(rawQuestionProgress.history) ? rawQuestionProgress.history.length : 0;
        const normalizedTimesShown = Math.max(
          rawQuestionProgress.timesShown ?? 0,
          rawQuestionProgress.attempts ?? 0,
          timesCorrect + timesIncorrect,
          historyLength,
        );
        const normalizedScore = rawQuestionProgress.currentScore ?? rawQuestionProgress.score ?? 0;

        const questionProgress = {
          ...rawQuestionProgress,
          currentScore: normalizedScore,
          score: normalizedScore,
          timesShown: normalizedTimesShown,
          attempts: normalizedTimesShown,
          timesCorrect,
          timesIncorrect,
          lastShown: rawQuestionProgress.lastShown ?? rawQuestionProgress.lastAttemptAt ?? null,
          lastAttemptAt: rawQuestionProgress.lastAttemptAt ?? rawQuestionProgress.lastShown ?? null,
          consecutiveCorrect: rawQuestionProgress.consecutiveCorrect ?? rawQuestionProgress.streak ?? 0,
        };

        return {
          question,
          progress: questionProgress,
        };
      });

    if (questionData.length === 0) {
      return null;
    }

    const sectionQuestionPool = questionData.map(({ question, progress: qp }) => ({
      ...question,
      sectionId: sectionId,
      currentScore: qp.currentScore ?? 0,
      score: qp.currentScore ?? 0,
      timesShown: qp.timesShown ?? 0,
      attempts: qp.timesShown ?? 0,
      lastShown: qp.lastShown ?? Date.now(),
      difficulty: typeof question.difficulty === 'string'
        ? (question.difficulty === 'hard' ? 1.0 : question.difficulty === 'medium' ? 0.5 : 0.2)
        : (question.difficulty || 0.5),
    }));

    const selected = selectQuestionFromSectionAlgorithm({ id: sectionId }, sectionQuestionPool);
    if (!selected) return null;

    return questions.find(q => q.id === selected.id) || selected;
  }

  /**
   * Check if section should be unlocked based on previous section score
   */
  async checkSectionUnlock(userId, courseId, completedSectionId) {
    const course = await this.courseRepository.getCourseById(courseId);
    const progress = await this.progressRepository.getUserProgress(userId, courseId);

    if (!course || !progress) {
      return null;
    }

    // Find the completed section
    const completedSection = course.getSectionById(completedSectionId);
    if (!completedSection) {
      return null;
    }

    // Get section score
    const sectionProgress = progress.getSectionProgress(completedSectionId);
    if (!sectionProgress || !sectionProgress.score) {
      return null;
    }

    // Check if score meets unlock threshold
    const unlockThreshold = completedSection.unlockThreshold || SECTION.UNLOCK_THRESHOLD;
    
    if (sectionProgress.score >= unlockThreshold) {
      // Find next section
      const nextSection = course.sections.find(s => s.index === completedSection.index + 1);
      
      if (nextSection && !progress.isSectionUnlocked(nextSection.id)) {
        // Unlock the section with validation
        progress.unlockSection(nextSection.id, course.sections);
        await this.progressRepository.updateUserProgress(progress);
        return nextSection.id;
      }
    }

    return null;
  }

  /**
   * Check if a specific question needs remediation based on consecutive misses
   * Triggers when the same question has been missed 3+ times in a row
   * @param {Object} progress - User progress object
   * @param {string} questionId - Question ID to check
   * @returns {boolean} True if remediation is needed for this question
   */
  checkQuestionRemediationNeeded(progress, questionId) {
    if (!progress) return false;
    const qp = progress.getQuestionProgress(questionId);
    if (!qp) return false;

    const history = Array.isArray(qp.history) ? qp.history : [];
    if (history.length < 3) return false;

    // Check last 3 attempts for consecutive incorrect
    // Handle both 'correct' (frontend) and 'isCorrect' (API response) field names
    const recent = history.slice(-3);
    const consecutiveMisses = recent.every(h => (h.correct === false || h.isCorrect === false));
    return consecutiveMisses;
  }

  /**
   * Get questions from a session's queue that need remediation
   * Returns question IDs that have 3+ consecutive misses
   * @param {Object} progress - User progress object
   * @param {Array} questionIds - Question IDs to check
   * @returns {string[]} Question IDs needing remediation
   */
  getQuestionsNeedingRemediation(progress, questionIds = []) {
    return questionIds.filter(qId => this.checkQuestionRemediationNeeded(progress, qId));
  }

  /**
   * Get learning recommendations for user
   */
  async getLearningRecommendations(userId, courseId) {
    const course = await this.courseRepository.getCourseById(courseId);
    const progress = await this.progressRepository.getUserProgress(userId, courseId);

    if (!course || !progress) {
      return null;
    }

    const recommendations = {
      nextSection: null,
      strugglingTopics: [],
      reviewQuestions: [],
      strengthAreas: [],
    };

    // Find next section to work on
    for (const section of course.sections) {
      if (!progress.isSectionUnlocked(section.id)) {
        continue;
      }
      if (!progress.isSectionCompleted(section.id)) {
        recommendations.nextSection = section;
        break;
      }
    }

    // Identify struggling topics (score < 60%)
    for (const [questionId, questionProgress] of Object.entries(progress.questionProgress)) {
      const score = questionProgress.currentScore ?? questionProgress.score ?? 0;
      if (score < 60) {
        const question = course.getQuestionById(questionId);
        if (question) {
          recommendations.strugglingTopics.push({
            question,
            score,
            attempts: questionProgress.timesShown ?? questionProgress.attempts ?? 0,
          });
        }
      }
    }

    // Identify questions needing review (decayed > 20%)
    for (const [questionId, questionProgress] of Object.entries(progress.questionProgress)) {
      const score = questionProgress.currentScore ?? questionProgress.score ?? 0;
      const lastAttemptAt = questionProgress.lastAttemptAt ?? questionProgress.lastShown;
      if (score >= 70 && lastAttemptAt) {
        const daysSinceAttempt = (Date.now() - new Date(lastAttemptAt)) / (1000 * 60 * 60 * 24);
        if (daysSinceAttempt > 7) { // Review after 7 days
          const question = course.getQuestionById(questionId);
          if (question) {
            recommendations.reviewQuestions.push({
              question,
              daysSinceAttempt,
              score,
            });
          }
        }
      }
    }

    // Identify strength areas (score >= 85%)
    for (const [questionId, questionProgress] of Object.entries(progress.questionProgress)) {
      const score = questionProgress.currentScore ?? questionProgress.score ?? 0;
      if (score >= 85) {
        const question = course.getQuestionById(questionId);
        if (question) {
          recommendations.strengthAreas.push({
            question,
            score,
            consecutiveCorrect: questionProgress.consecutiveCorrect ?? questionProgress.streak ?? 0,
          });
        }
      }
    }

    return recommendations;
  }
}

// Export singleton instance
export const adaptiveEngine = new AdaptiveEngine();
